import React, { useState } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import LogLevelChip from './LogLevelChip';
import LogChannelChip from './LogChannelChip';

const isPresent = (value) => value !== undefined && value !== null;

/**
 * Formata o 'ts' (ISO 8601 com fuso do servidor) como HH:mm:ss, prefixando com a
 * data (DD/MM) quando o dia do evento é diferente do dia filtrado — sem passar por
 * Date/fuso do navegador, já que os arquivos são agrupados pelo dia no fuso do servidor.
 * @param {string} ts
 * @param {string} filterDate - YYYY-MM-DD
 * @returns {string}
 */
function formatLogTime(ts, filterDate) {
  if (!ts || ts.length < 19) return ts || '—';
  const datePart = ts.slice(0, 10);
  const timePart = ts.slice(11, 19);
  if (filterDate && datePart !== filterDate) {
    const [, month, day] = datePart.split('-');
    return `${day}/${month} ${timePart}`;
  }
  return timePart;
}

/**
 * LogRow - Linha da tabela de logs; clique expande ip/user/method/ctx (Admin Geral > Logs)
 * @param {Object} props
 * @param {Object} props.log - ts, level, channel, msg, company?, user?, ip, endpoint, method, req, ctx?
 * @param {string} props.date - dia filtrado, usado para decidir se mostra a data junto da hora
 * @param {Function} props.onFilterReq - (req: string) => void
 * @param {Function} props.onFilterCompany - (company: number|string) => void
 */
function LogRow({ log, date, onFilterReq, onFilterCompany }) {
  const [expanded, setExpanded] = useState(false);
  const { ts, level, channel, msg, company, user, ip, endpoint, method, req, ctx } = log;

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => setExpanded((prev) => !prev)}>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.75rem' }}>
            {formatLogTime(ts, date)}
          </Typography>
        </TableCell>

        <TableCell><LogLevelChip level={level} /></TableCell>

        <TableCell><LogChannelChip channel={channel} /></TableCell>

        <TableCell>
          <Typography variant="body2" sx={{ color: 'text.primary', maxWidth: 360 }} noWrap>{msg}</Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 220 }} noWrap>{endpoint || '—'}</Typography>
        </TableCell>

        <TableCell onClick={(e) => e.stopPropagation()}>
          {isPresent(company) ? (
            <Chip
              size="small"
              label={company}
              clickable
              onClick={() => onFilterCompany && onFilterCompany(company)}
              sx={{ height: 22, fontSize: '0.75rem' }}
            />
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          )}
        </TableCell>

        <TableCell onClick={(e) => e.stopPropagation()}>
          {req ? (
            <Chip
              size="small"
              label={req}
              clickable
              onClick={() => onFilterReq && onFilterReq(req)}
              sx={{ height: 22, fontSize: '0.6875rem', fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
            />
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, border: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#F8F9FC' }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: ctx ? 1.5 : 0 }}>
                <Typography variant="body2"><strong>IP:</strong> {ip || '—'}</Typography>
                <Typography variant="body2"><strong>Usuário:</strong> {isPresent(user) ? user : '—'}</Typography>
                <Typography variant="body2"><strong>Método:</strong> {method || '—'}</Typography>
              </Box>
              {ctx && (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: '#0B0F14',
                    color: '#D6E2EF',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(ctx, null, 2)}
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default LogRow;
